import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Token } from '@bcef/ia-shared';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { catchError, EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';

import { globalActions } from '@features/global/store/actions';
import { RoutingPaths } from '@features/reconciliation/enums';
import { ReconciliationService } from '@features/reconciliation/services/reconciliation.service';

import { reconciliationActions } from '../actions';
import * as fromSelectors from '../selectors';

@Injectable()
export class ReconciliationEffects {
  private readonly actions$: Actions = inject(Actions);

  private readonly reconciliationService: ReconciliationService = inject(ReconciliationService);

  private readonly store: Store = inject(Store);

  private readonly router: Router = inject(Router);

  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  public UrlParamsAdd$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(reconciliationActions.urlParamsAdd),
      map(({ urlParams }) => {
        return urlParams.numReservation && urlParams.code
          ? reconciliationActions.urlParamsAddCompleted({ urlParams })
          : reconciliationActions.urlParamsAddError({ error: { urlParam: true } });
      }),
    );
  });

  public TokenAdd$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(reconciliationActions.urlParamsAddCompleted),
      concatLatestFrom(() => this.store.select(fromSelectors.selectTokenResponse)),
      map(([urlParams, tokenResponse]) => {
        if (tokenResponse.access_token !== '') {
          return reconciliationActions.tokenAddCompleted({ tokenResponse: { ...tokenResponse } });
        }
        return reconciliationActions.tokenAdd(urlParams);
      }),
    );
  });

  public GetToken$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(reconciliationActions.tokenAdd),
      switchMap(({ urlParams }) =>
        this.reconciliationService.getToken(urlParams).pipe(
          map((tokenResponse: Token) => {
            return reconciliationActions.tokenAddCompleted({ tokenResponse });
          }),
          catchError((error) => {
            return of(reconciliationActions.tokenAddError({ error }));
          }),
        ),
      ),
    );
  });

  public ReconciliateAdd$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(reconciliationActions.tokenAddCompleted),
      concatLatestFrom(() => this.store.select(fromSelectors.selectUrlParams)),
      map(([, urlParams]) => globalActions.reconciliateAdd({ urlParams })),
    );
  });

  public navigateToErrorPageFromUrlParamsError$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(reconciliationActions.urlParamsAddError),
        tap(() => {
          this.router.navigate([RoutingPaths.ERROR_PAGE], {
            skipLocationChange: true,
            queryParams: this.activatedRoute.snapshot.queryParams,
          });
          return EMPTY;
        }),
      );
    },
    { dispatch: false },
  );

  public navigateToErrorPageFromTokenError$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(reconciliationActions.tokenAddError),
        tap(() => {
          this.router.navigate([RoutingPaths.ERROR_PAGE], {
            skipLocationChange: true,
            queryParams: this.activatedRoute.snapshot.queryParams,
          });
          return EMPTY;
        }),
      );
    },
    { dispatch: false },
  );

  public navigateToErrorPageFromReconciliationError$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(globalActions.reconciliateError),
        tap(() => {
          this.router.navigate([RoutingPaths.ERROR_PAGE], {
            skipLocationChange: true,
            queryParams: this.activatedRoute.snapshot.queryParams,
          });
          return EMPTY;
        }),
      );
    },
    { dispatch: false },
  );
}
